import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2, ShieldOff } from "lucide-react";
import { AuthProvider, useAuth } from "@/lib/auth";
import { canAccessPage } from "@/lib/rbac";

import Landing from "@/pages/Landing";
import LoginPage from "@/pages/LoginPage";
import AdminShell from "@/pages/AdminShell";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminTransactions from "@/pages/admin/AdminTransactions";
import AdminApprovals from "@/pages/admin/AdminApprovals";
import AdminInvoices from "@/pages/admin/AdminInvoices";
import AdminSecurity from "@/pages/admin/AdminSecurity";
import PartnerShell from "@/pages/PartnerShell";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import ChannelsPage from "@/pages/partner/ChannelsPage";
import PayoutsPage from "@/pages/partner/PayoutsPage";
import PartnerPanelShell from "@/pages/partner/panel/PartnerPanelShell";
import PanelDashboard from "@/pages/partner/panel/PanelDashboard";
import PanelFunding from "@/pages/partner/panel/PanelFunding";
import PanelPayouts from "@/pages/partner/panel/PanelPayouts";
import PanelBeneficiaries from "@/pages/partner/panel/PanelBeneficiaries";
import PanelSettings from "@/pages/partner/panel/PanelSettings";
import PanelApi from "@/pages/partner/panel/PanelApi";
import PanelWebhookSigning from "@/pages/partner/panel/PanelWebhookSigning";
import PanelVirtualAccount from "@/pages/partner/panel/PanelVirtualAccount";
import PanelStatement from "@/pages/partner/panel/PanelStatement";
import PanelSupport from "@/pages/partner/panel/PanelSupport";
import PublicApiDocs from "@/pages/PublicApiDocs";
import PanelPayin from "@/pages/partner/panel/PanelPayin";
import PanelCreatePayin from "@/pages/partner/panel/PanelCreatePayin";
import PanelPayinHistory from "@/pages/partner/panel/PanelPayinHistory";
import PayinAdminPage from "@/pages/admin/PayinAdminPage";
import PayoutsAdminPage from "@/pages/admin/PayoutsAdminPage";
import VendorsAdminPage from "@/pages/admin/VendorsAdminPage";
import AdminUsersPage from "@/pages/admin/AdminUsersPage";
import AuditLogPage from "@/pages/admin/AuditLogPage";
import { UniversalLink, ApiCredentials, Team, Profile } from "@/pages/partner/PartnerExtras";
import WebhooksPage from "@/pages/partner/WebhooksPage";
import CustomersPage from "@/pages/CustomersPage";
import { SmsWhitelist, BankStatements } from "@/pages/partner/PartnerVerify";
import PublicPay from "@/pages/PublicPay";
import PaymentPage from "@/pages/PaymentPage";
import PartnerPaymentLinks from "@/pages/partner/PartnerPaymentLinks";
import PartnerWebsites from "@/pages/partner/PartnerWebsites";
import EmbedCustomizer from "@/pages/partner/EmbedCustomizer";
import PartnerExperiments from "@/pages/partner/PartnerExperiments";
import SuccessfulTransactions from "@/pages/partner/SuccessfulTransactions";
import PartnerAnalyticsPage from "@/pages/partner/PartnerAnalyticsPage";
import PartnerAlerts from "@/pages/partner/PartnerAlerts";
import PartnerAA from "@/pages/partner/PartnerAA";
import AdminAlerts from "@/pages/admin/AdminAlerts";
import HostRedirector from "@/components/HostRedirector";

function FullLoader() {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div>;
}

function Protected({ kind, children }) {
    const { user, loading } = useAuth();
    if (loading) return <FullLoader />;
    if (!user) return <Navigate to={kind === "admin" ? "/admin/login" : "/partner/login"} replace />;
    if (user.ut !== kind) return <Navigate to={user.ut === "admin" ? "/admin" : "/partner"} replace />;
    return children;
}

/**
 * Per-page RBAC guard. Wraps an Outlet child so the SAME permission
 * logic the sidebar uses (canAccessPage) also gates the route. Without
 * this, an agent (`partner_team`) could type the URL directly and see
 * a page the sidebar hides from them — which was the actual security
 * issue reported on 26 Jun 2026.
 *
 * Props mirror the NAV table shape: { perm, adminOnly, gated, always }.
 * Children render only when canAccessPage(user, props) is true.
 */
function PageGuard({ perm, adminOnly, gated, always, children }) {
    const { user, loading } = useAuth();
    if (loading) return <FullLoader />;
    if (!user) return null; // Parent <Protected> handles the redirect.
    const allowed = canAccessPage(user, { perm, adminOnly, gated, always });
    if (allowed) return children;
    return <ForbiddenInline />;
}

/** Minimal in-page 403 — kept inline so we don't add a new route */
function ForbiddenInline() {
    return (
        <div data-testid="rbac-forbidden" className="min-h-[60vh] flex items-center justify-center p-8">
            <div className="text-center max-w-sm">
                <ShieldOff className="w-10 h-10 text-rose-500 mx-auto mb-3" />
                <h2 className="text-lg font-semibold">Access restricted</h2>
                <p className="text-sm text-slate-500 mt-1">
                    Your role doesn&apos;t have permission to view this page.
                    Ask your partner admin to grant access in <span className="font-mono">Team → Permissions</span>.
                </p>
            </div>
        </div>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <HostRedirector />
                <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/admin/login" element={<LoginPage kind="admin" />} />
                    <Route path="/partner/login" element={<LoginPage kind="partner" />} />

                    <Route path="/admin" element={<Protected kind="admin"><AdminShell /></Protected>}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="partners" element={<AdminPartners />} />
                        <Route path="transactions" element={<AdminTransactions />} />
                        <Route path="approvals" element={<AdminApprovals scope="admin" />} />
                        <Route path="customers" element={<CustomersPage scope="admin" />} />
                        <Route path="invoices" element={<AdminInvoices scope="admin" />} />
                        <Route path="alerts" element={<AdminAlerts />} />
                        <Route path="security" element={<AdminSecurity />} />
                        <Route path="payouts" element={<PayoutsAdminPage />} />
                        <Route path="payin" element={<PayinAdminPage />} />
                        <Route path="vendors" element={<VendorsAdminPage />} />
                        <Route path="users" element={<AdminUsersPage />} />
                        <Route path="audit" element={<AuditLogPage />} />
                    </Route>

                    <Route path="/partner" element={<Protected kind="partner"><PartnerShell /></Protected>}>
                        <Route index element={<PageGuard perm="dashboard"><PartnerDashboard /></PageGuard>} />
                        <Route path="upi" element={<PageGuard perm="upi"><ChannelsPage kind="upi" /></PageGuard>} />
                        <Route path="banks" element={<PageGuard perm="banks"><ChannelsPage kind="bank" /></PageGuard>} />
                        <Route path="transactions" element={<PageGuard perm="transactions"><AdminTransactions /></PageGuard>} />
                        <Route path="success" element={<PageGuard perm="success_txns"><SuccessfulTransactions /></PageGuard>} />
                        <Route path="approvals" element={<PageGuard perm="approvals"><AdminApprovals scope="partner" /></PageGuard>} />
                        <Route path="customers" element={<PageGuard perm="customers"><CustomersPage scope="partner" /></PageGuard>} />
                        <Route path="sms" element={<PageGuard perm="sms"><SmsWhitelist /></PageGuard>} />
                        <Route path="statements" element={<PageGuard perm="statements"><BankStatements /></PageGuard>} />
                        <Route path="link" element={<PageGuard perm="link"><UniversalLink /></PageGuard>} />
                        <Route path="links" element={<PageGuard perm="links"><PartnerPaymentLinks /></PageGuard>} />
                        <Route path="websites" element={<PageGuard perm="links"><PartnerWebsites /></PageGuard>} />
                        <Route path="links/:linkId/embed" element={<PageGuard perm="links"><EmbedCustomizer /></PageGuard>} />
                        <Route path="experiments" element={<PageGuard perm="links"><PartnerExperiments /></PageGuard>} />
                        <Route path="analytics" element={<PageGuard perm="analytics"><PartnerAnalyticsPage /></PageGuard>} />
                        {/* Team management is partner_admin only. PageGuard's adminOnly flag mirrors the NAV table. */}
                        <Route path="team" element={<PageGuard adminOnly><Team /></PageGuard>} />
                        <Route path="credentials" element={<PageGuard perm="credentials"><ApiCredentials /></PageGuard>} />
                        <Route path="webhooks" element={<PageGuard perm="webhooks"><WebhooksPage /></PageGuard>} />
                        <Route path="invoices" element={<PageGuard perm="invoices"><AdminInvoices scope="partner" /></PageGuard>} />
                        {/* Profile is always visible — every authenticated user can manage their own account. */}
                        <Route path="profile" element={<PageGuard always><Profile /></PageGuard>} />
                        <Route path="alerts" element={<PageGuard perm="alerts"><PartnerAlerts /></PageGuard>} />
                        <Route path="aa" element={<PageGuard perm="aa" gated="aa_enabled"><PartnerAA /></PageGuard>} />
                        <Route path="payouts" element={<PageGuard always><PayoutsPage /></PageGuard>} />
                    </Route>

                    {/* Partner payouts panel at /partner/panel/* — unbranded, generic 'Payouts Portal'. */}
                    <Route path="/partner/panel" element={<Protected kind="partner"><PartnerPanelShell /></Protected>}>
                        <Route index element={<PanelDashboard />} />
                        <Route path="beneficiaries" element={<PanelBeneficiaries />} />
                        <Route path="create-payin" element={<PanelCreatePayin />} />
                        <Route path="payin-history" element={<PanelPayinHistory />} />
                        <Route path="collect" element={<PanelPayin />} />
                        <Route path="funding" element={<PanelFunding />} />
                        <Route path="payouts" element={<PanelPayouts />} />
                        <Route path="send" element={<PageGuard always><PayoutsPage /></PageGuard>} />
                        <Route path="virtual-account" element={<PanelVirtualAccount />} />
                        <Route path="statement" element={<PanelStatement />} />
                        <Route path="support" element={<PanelSupport />} />
                        <Route path="api" element={<PanelApi />} />
                        <Route path="webhook-signing" element={<PanelWebhookSigning />} />
                        <Route path="settings" element={<PanelSettings />} />
                    </Route>

                    <Route path="/docs" element={<PublicApiDocs />} />

                    <Route path="/pay/:linkCode" element={<PublicPay />} />
                    <Route path="/p/:linkCode" element={<PublicPay />} />
                    <Route path="/payment/:txnId" element={<PaymentPage />} />

                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
        </AuthProvider>
    );
}
